// Module-local tabs; business access and screen state remain with the owning screen.
import React from "react";
import { cx } from "./cx";

export interface SectionNavEntry { id: string; label: string; danger?: boolean; }
const PanelContext = React.createContext<string | null>(null);

export function SectionNav({ entries, active, onSelect, ariaLabel, panelId: explicitPanelId }: {
  entries: SectionNavEntry[];
  active: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
  panelId?: string;
}) {
  const contextId = React.useContext(PanelContext);
  const panelId = explicitPanelId ?? contextId;
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const select = (index: number) => {
    const entry = entries[index];
    onSelect(entry.id);
    // QA changes owning screens between its tabs; focus the replacement after it mounts.
    window.requestAnimationFrame(() => {
      const tab = panelId ? document.getElementById(`${panelId}-tab-${entry.id}`) : refs.current[index];
      tab?.focus();
    });
  };
  return <div className="module-tabs" role="tablist" aria-label={ariaLabel}>
    {entries.map((entry, index) => <button key={entry.id} ref={(node) => { refs.current[index] = node; }}
      type="button" role="tab" id={panelId ? `${panelId}-tab-${entry.id}` : undefined}
      aria-controls={panelId ?? undefined} aria-selected={active === entry.id} tabIndex={active === entry.id || (!entries.some((item) => item.id === active) && index === 0) ? 0 : -1}
      className={cx("module-tab", active === entry.id && "is-active", entry.danger && "module-tab--danger")}
      onClick={() => select(index)} onKeyDown={(event) => {
        const next = event.key === "Home" ? 0 : event.key === "End" ? entries.length - 1
          : event.key === "ArrowRight" ? (index + 1) % entries.length
            : event.key === "ArrowLeft" ? (index - 1 + entries.length) % entries.length : null;
        if (next === null) return;
        event.preventDefault(); select(next);
      }}>{entry.label}</button>)}
  </div>;
}

/** A single active panel; owning screens may keep visited forms mounted inside it. */
export function SectionLayout({ nav, title, active, children }: {
  nav: React.ReactNode;
  title: string;
  active: string;
  children: React.ReactNode;
}) {
  const panelId = React.useId();
  return <PanelContext.Provider value={panelId}>
    <div className="seclayout">
      {nav}
      <div className="seclayout__content" id={panelId} role="tabpanel" aria-labelledby={`${panelId}-tab-${active}`} tabIndex={0}>
        <h2 className="seclayout__title">{title}</h2>
        {children}
      </div>
    </div>
  </PanelContext.Provider>;
}
